import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { ExternalLink } from 'lucide-react';

export default function AdBanner({ ad, variant = 'default' }) {
  const navigate = useNavigate();

  if (!ad) return null;

  async function handleClick() {
    try {
      await api.post('/ads/' + ad.id + '/click');
    } catch (err) {
      // Silent fail for tracking
    }

    if (ad.link_url.startsWith('/')) {
      navigate(ad.link_url);
    } else {
      window.open(ad.link_url, '_blank');
    }
  }

  if (variant === 'inline') {
    return (
      <div 
        onClick={handleClick}
        data-testid={'ad-' + ad.id}
        className="group cursor-pointer bg-surface/50 border border-border/50 rounded-xl overflow-hidden hover:border-ruby/30 transition-all duration-300"
      >
        <div className="flex items-center gap-4 p-4">
          <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
            <img 
              src={ad.image_url} 
              alt={ad.title}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wider">Patrocinado</p>
            <h4 className="font-medium text-foreground truncate group-hover:text-ruby transition-colors">
              {ad.title}
            </h4>
            {ad.description && (
              <p className="text-sm text-muted-foreground truncate">{ad.description}</p>
            )}
          </div>
          <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-ruby flex-shrink-0" />
        </div>
      </div>
    );
  }

  if (variant === 'sidebar') {
    return (
      <div 
        onClick={handleClick}
        data-testid={'ad-' + ad.id}
        className="group cursor-pointer bg-surface border border-border rounded-xl overflow-hidden hover:border-ruby/30 transition-all duration-300"
      >
        <div className="aspect-video relative overflow-hidden">
          <img 
            src={ad.image_url} 
            alt={ad.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <span className="absolute top-2 right-2 text-[10px] text-white/60 bg-black/40 px-2 py-0.5 rounded-full">
            Patrocinado
          </span>
        </div>
        <div className="p-4">
          <h4 className="font-medium text-foreground mb-1 group-hover:text-ruby transition-colors">
            {ad.title}
          </h4>
          {ad.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">{ad.description}</p>
          )}
        </div>
      </div>
    );
  }

  // Default banner variant
  return (
    <div 
      onClick={handleClick}
      data-testid={'ad-' + ad.id}
      className="group cursor-pointer relative rounded-2xl overflow-hidden hover:shadow-[0_0_30px_rgba(155,17,30,0.2)] transition-all duration-300"
    >
      <div className="relative h-40 md:h-48">
        <img 
          src={ad.image_url} 
          alt={ad.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
        
        <div className="absolute inset-0 flex items-center p-6 md:p-8">
          <div className="max-w-lg">
            <span className="inline-block text-[10px] text-white/60 bg-white/10 px-2 py-1 rounded-full mb-3 uppercase tracking-wider">
              Patrocinado
            </span>
            <h3 className="font-brand text-xl md:text-2xl font-semibold text-foreground mb-2 group-hover:text-ruby-light transition-colors">
              {ad.title}
            </h3>
            {ad.description && (
              <p className="text-muted-foreground text-sm md:text-base line-clamp-2">
                {ad.description}
              </p>
            )}
          </div>
        </div>
        
        <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="flex items-center gap-1 text-sm text-white bg-ruby/80 px-3 py-1.5 rounded-full">
            Saiba mais <ExternalLink className="w-3 h-3" />
          </span>
        </div>
      </div>
    </div>
  );
}
